import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { bookingApi } from '../services/api';
import { buildWhatsAppLink } from '../utils/whatsapp';
import InbuiltWhatsAppModal from '../components/InbuiltWhatsAppModal';
import BookingActionModal from '../components/BookingActionModal';

const TherapistBookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [whatsappTarget, setWhatsappTarget] = useState(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionModalBooking, setActionModalBooking] = useState(null);
  const [actionModalType, setActionModalType] = useState('reschedule');

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const res = await bookingApi.mine();
      setBookings(res.bookings || res.data || res || []);
    } catch (err) {
      console.error('Failed to load bookings:', err);
      setError(err.message || 'Unable to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleUpdateStatus = async (bookingId, status) => {
    try {
      await bookingApi.updateStatus(bookingId, { status });
      await fetchBookings();
    } catch (err) {
      console.error('Failed to update booking status:', err);
      alert(err.message || 'Unable to update status');
    }
  };

  return (
    <>
      <DashboardSidebar userRole="physiotherapist" />
      <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '40px 20px', marginLeft: '280px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ marginBottom: '40px' }}>
            <Link to="/physio-dashboard" style={{ color: '#667eea', textDecoration: 'none', marginBottom: '20px', display: 'inline-block', fontWeight: 600 }}>
              ← Back to Dashboard
            </Link>
            <h1 style={{ fontSize: '32px', color: '#333', margin: '0 0 10px 0' }}>Patient Bookings & Appointments</h1>
            <p style={{ color: '#666', margin: 0 }}>Review session requests, accept, reschedule, or cancel bookings</p>
          </div>

          {error && (
            <div style={{ padding: '12px 16px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '20px' }}>
              {error}
            </div>
          )}

          {/* Bookings List */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            {loading ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>Loading bookings...</p>
            ) : bookings.length > 0 ? (
              <div style={{ display: 'grid', gap: '20px' }}>
                {bookings.map((booking) => {
                  const clientName = booking.clientId?.name || booking.clientName || 'Patient';
                  const clientPhone = booking.clientId?.phone || booking.clientPhone || '';
                  const clientEmail = booking.clientId?.email || '';
                  const scheduledDateStr = booking.scheduledAt
                    ? new Date(booking.scheduledAt).toLocaleString('en-IN', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'Flexible / TBD';

                  return (
                    <div
                      key={booking._id}
                      style={{
                        padding: '24px',
                        border: booking.status === 'pending' ? '1.5px solid #93c5fd' : '1px solid #e5e7eb',
                        borderRadius: '12px',
                        background: '#fff',
                        boxShadow: booking.status === 'pending' ? '0 4px 12px rgba(59, 130, 246, 0.08)' : '0 2px 6px rgba(0,0,0,0.02)',
                      }}
                    >
                      {/* 🔔 Prominent Pending Request Banner for Therapist */}
                      {booking.status === 'pending' && (
                        <div style={{
                          marginBottom: '18px',
                          padding: '14px 18px',
                          background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
                          border: '1.5px solid #93c5fd',
                          borderRadius: '10px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '12px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '24px' }}>🔔</span>
                            <div>
                              <div style={{ fontWeight: 800, color: '#1e40af', fontSize: '14px' }}>
                                New Booking Request — Confirmation Required
                              </div>
                              <div style={{ color: '#3730a3', fontSize: '12px' }}>
                                Patient {clientName} is waiting for your response. Accept the session, reschedule to a better slot, or decline.
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleUpdateStatus(booking._id, 'confirmed')}
                              style={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: '#ffffff',
                                padding: '8px 18px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 3px 8px rgba(16,185,129,0.3)',
                              }}
                            >
                              ✓ Accept Request
                            </button>
                            <button
                              onClick={() => {
                                setActionModalBooking(booking);
                                setActionModalType('reschedule');
                                setActionModalOpen(true);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                                color: '#ffffff',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 3px 8px rgba(124,58,237,0.3)',
                              }}
                            >
                              🔄 Reschedule Slot
                            </button>
                            <button
                              onClick={() => {
                                setActionModalBooking(booking);
                                setActionModalType('cancel');
                                setActionModalOpen(true);
                              }}
                              style={{
                                background: '#ffffff',
                                color: '#dc2626',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: '1.5px solid #fca5a5',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 5px rgba(220,38,38,0.1)',
                              }}
                            >
                              🚫 Decline / Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                          <div
                            style={{
                              width: '54px',
                              height: '54px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '22px',
                            }}
                          >
                            {clientName.charAt(0)}
                          </div>
                          <div>
                            <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#111827' }}>
                              👤 {clientName}
                            </h3>
                            <p style={{ margin: '0 0 4px 0', color: '#4b5563', fontSize: '14px' }}>
                              📅 <strong>Session:</strong> {scheduledDateStr}
                            </p>
                            <p style={{ margin: 0, color: '#6b7280', fontSize: '13px' }}>
                              📧 {clientEmail} | 📞 {clientPhone || 'N/A'}
                            </p>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span
                            style={{
                              padding: '6px 14px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 700,
                              textTransform: 'capitalize',
                              backgroundColor:
                                booking.status === 'confirmed' || booking.status === 'completed'
                                  ? '#d1fae5'
                                  : booking.status === 'pending'
                                  ? '#fef3c7'
                                  : '#fee2e2',
                              color:
                                booking.status === 'confirmed' || booking.status === 'completed'
                                  ? '#065f46'
                                  : booking.status === 'pending'
                                  ? '#92400e'
                                  : '#991b1b',
                            }}
                          >
                            {booking.status}
                          </span>
                          <span
                            style={{
                              padding: '6px 14px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 700,
                              backgroundColor: '#e0e7ff',
                              color: '#3730a3',
                            }}
                          >
                            {booking.mode === 'online' ? '💻 Online Session' : '🏥 In-Person'}
                          </span>
                        </div>
                      </div>

                      {/* Notes / History */}
                      {booking.notes && (
                        <div style={{ marginTop: '12px', fontSize: '13px', color: '#4b5563', background: '#f9fafb', padding: '8px 12px', borderRadius: '6px' }}>
                          💬 <strong>Patient Notes:</strong> {booking.notes}
                        </div>
                      )}

                      {booking.rescheduleReason && (
                        <div style={{ marginTop: '10px', padding: '8px 12px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '6px', fontSize: '12px', color: '#6d28d9' }}>
                          🔄 <strong>Reschedule Note ({booking.rescheduledBy === 'physiotherapist' ? 'You' : booking.rescheduledBy === 'client' ? 'Patient' : booking.rescheduledBy || 'Updated'}):</strong> {booking.rescheduleReason}
                        </div>
                      )}

                      {booking.cancellationReason && (
                        <div style={{ marginTop: '10px', padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '12px', color: '#b91c1c' }}>
                          🚫 <strong>Cancellation Note ({booking.cancelledBy === 'physiotherapist' ? 'You' : booking.cancelledBy === 'client' ? 'Patient' : booking.cancelledBy || 'Cancelled'}):</strong> {booking.cancellationReason}
                        </div>
                      )}

                      {/* Action Bar */}
                      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {booking.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleUpdateStatus(booking._id, 'confirmed')}
                              style={{
                                background: '#10b981',
                                color: '#fff',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 6px rgba(16,185,129,0.3)',
                              }}
                            >
                              ✓ Accept Request
                            </button>

                            <button
                              onClick={() => {
                                setActionModalBooking(booking);
                                setActionModalType('reschedule');
                                setActionModalOpen(true);
                              }}
                              style={{
                                background: '#7c3aed',
                                color: '#fff',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 6px rgba(124,58,237,0.3)',
                              }}
                            >
                              🔄 Reschedule
                            </button>

                            <button
                              onClick={() => {
                                setActionModalBooking(booking);
                                setActionModalType('cancel');
                                setActionModalOpen(true);
                              }}
                              style={{
                                background: '#ef4444',
                                color: '#fff',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 6px rgba(239,68,68,0.3)',
                              }}
                            >
                              ✕ Decline / Cancel
                            </button>
                          </>
                        )}

                        {/* WhatsApp Communication */}
                        <button
                          onClick={() => {
                            setWhatsappTarget({
                              _id: booking.clientId?._id || booking.clientId,
                              name: clientName,
                              phone: clientPhone,
                              email: clientEmail,
                              role: 'client',
                              status: booking.status,
                              isSessionCompleted: booking.status === 'completed',
                            });
                            setWhatsappModalOpen(true);
                          }}
                          style={{
                            background: '#00a884',
                            color: '#fff',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            fontWeight: 700,
                            fontSize: '13px',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 6px rgba(0,168,132,0.25)',
                          }}
                        >
                          💬 Inbuilt WhatsApp
                        </button>

                        {clientPhone && (
                          <a
                            href={buildWhatsAppLink(clientPhone, `Hello ${clientName}, I am reaching out regarding your therapy session on PsychMitra for ${scheduledDateStr}.`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              background: '#25D366',
                              color: '#fff',
                              padding: '8px 16px',
                              borderRadius: '20px',
                              fontWeight: 700,
                              fontSize: '13px',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            📲 External App
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
                <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>📅</span>
                <h3>No Bookings Yet</h3>
                <p>When clients book sessions with you, they will appear here.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <InbuiltWhatsAppModal
        isOpen={whatsappModalOpen}
        onClose={() => setWhatsappModalOpen(false)}
        targetUser={whatsappTarget}
        contactsList={bookings.map((b) => {
          const client = b.clientId && typeof b.clientId === 'object' ? b.clientId : {};
          return {
            _id: client._id || b.clientId,
            name: client.name || b.clientName || 'Patient',
            phone: client.phone || b.clientPhone || '',
            email: client.email || b.clientEmail || '',
            role: 'client',
            status: b.status,
            isSessionCompleted: b.status === 'completed',
          };
        })}
      />

      <BookingActionModal
        isOpen={actionModalOpen}
        onClose={() => setActionModalOpen(false)}
        booking={actionModalBooking}
        actionType={actionModalType}
        userRole="therapist"
        onSuccess={() => {
          fetchBookings();
        }}
      />
    </>
  );
};

export default TherapistBookingsPage;
